import api from '../utils/api';

/**
 * Serviço para gerenciar acesso a perfis públicos de talentos
 * Requer autenticação da empresa para gerar tokens
 */
class PublicProfileService {
  /**
   * Gera token para acessar perfil público do talento
   * @param {string} talentSlug - Slug do talento
   * @param {number} expiresIn - Tempo de expiração em horas (padrão: 24)
   * @returns {Promise<Object>} Objeto com token, profileUrl e expiresAt
   */
  async generateToken(talentSlug, expiresIn = 24) {
    try {
      const tokenResponse = await api.post('/companies/public_profile_tokens', {
        talent_slug: talentSlug,
        expires_in: expiresIn
      });

      const { token, expires_at } = tokenResponse.data;

      // Montar URL do perfil público com o token
      const profileUrl = `/talents/profile/${talentSlug}?token=${token}`;

      return {
        token: token,
        profileUrl: profileUrl,
        expiresAt: expires_at
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Busca dados do perfil público usando token
   * @param {string} talentSlug - Slug do talento
   * @param {string} token - Token JWT válido
   * @returns {Promise<Object>} Dados do perfil
   */
  async getProfile(talentSlug, token) {
    try {
      const response = await api.get(
        `/public/talents/${talentSlug}?token=${token}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Extrai token da URL se existir
   * @param {string} url - URL atual
   * @returns {string|null} Token ou null
   */
  getTokenFromUrl(url = window.location.href) {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('token');
    } catch (error) {
      return null;
    }
  }

  /**
   * Trata erros da API de forma amigável
   * @param {Error} error - Erro da requisição
   * @returns {Error} Erro formatado com mensagem amigável
   */
  handleError(error) {
    const status = error.response?.status;
    const errorData = error.response?.data;

    if (status === 401) {
      const message = errorData?.message || errorData?.error;

      if (message?.includes('Token de acesso necessário')) {
        return new Error('É necessário estar autenticado como empresa para acessar este perfil.');
      }
      if (message?.includes('Token inválido ou expirado')) {
        return new Error('O link expirou. Por favor, gere um novo link de acesso.');
      }
      if (message?.includes('Token não autorizado')) {
        return new Error('Este token não é válido para acessar este perfil.');
      }

      return new Error('Token inválido ou expirado. Por favor, faça login novamente.');
    }

    if (status === 404) {
      const message = errorData?.message || errorData?.error;

      if (message?.includes('não disponível publicamente')) {
        return new Error('Este perfil não está disponível publicamente.');
      }

      return new Error('Talento não encontrado.');
    }

    if (status === 403) {
      return new Error('Este perfil não está disponível publicamente.');
    }

    if (status === 500) {
      return new Error('Erro no servidor. Por favor, tente novamente mais tarde.');
    }

    if (!error.response) {
      return new Error('Erro de conexão. Verifique se o backend está rodando.');
    }

    return new Error(errorData?.message || errorData?.error || 'Erro ao carregar perfil. Tente novamente.');
  }

  /**
   * Verifica se o token está expirado baseado na data
   * @param {string} expiresAt - Data de expiração ISO
   * @returns {boolean} true se expirado
   */
  isTokenExpired(expiresAt) {
    if (!expiresAt) return true;
    return new Date(expiresAt) < new Date();
  }
}

// Exportar instância única (singleton)
const publicProfileService = new PublicProfileService();
export default publicProfileService;
